import React from 'react';
import { Trophy, Shield, Search, ChevronRight, ExternalLink, Mail, Key, Clock } from 'lucide-react';

const Challenges = () => {
  const challenges = [
    {
      id: 1,
      title: 'Introduction to Blue Team Quiz',
      difficulty: 'Beginner',
      description: 'Test your foundational knowledge of defensive security concepts and Blue Team fundamentals.',
      category: 'Knowledge Check',
      icon: Shield,
      url: 'https://forms.gle/fYtkfKQL65ePqa7c9',
      color: 'from-green-500 to-emerald-500',
      status: 'active',
    },
    {
      id: 2,
      title: 'Tinder-phish',
      difficulty: 'Intermediate',
      description: 'Swipe right if phished, left if safe! Test your email security skills by identifying phishing attempts.',
      category: 'Phishing Detection',
      icon: Mail,
      url: 'https://phish.blueteamvillageatvizag.live/',
      color: 'from-pink-500 to-rose-500',
      status: 'active',
    },
    {
      id: 3,
      title: 'Elastic Search Challenge',
      difficulty: 'Advanced',
      description: 'Hunt for security flags in Elasticsearch logs. Master SIEM queries and log analysis techniques.',
      category: 'SIEM & Log Analysis',
      icon: Search,
      url: 'http://104.156.255.186:5601',
      credentials: {
        username: 'elastic',
        password: 't-dQ2WgDRJ=JN5jV-G5E',
      },
      cheatsheet: 'https://elasticsearch-cheatsheet.jolicode.com/',
      submitUrl: 'https://form.typeform.com/to/lCdzRDh9',
      color: 'from-purple-500 to-indigo-500',
      status: 'active',
    },
    {
      id: 4,
      title: 'Mystery Challenge',
      difficulty: 'Coming Soon',
      description: 'Something exciting is brewing in our security lab. Stay tuned for the next challenge!',
      category: 'TBA',
      icon: Clock,
      color: 'from-gray-500 to-gray-600',
      status: 'coming-soon',
    },
  ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'Intermediate':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'Advanced':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 font-mono">
            <span className="text-accent">&gt;</span> Challenges
          </h1>
          
          {/* Catchy Hook */}
          <div className="bg-gradient-to-r from-accent/10 via-accent-blue/10 to-accent/10 border-2 border-accent/30 rounded-lg p-8 mb-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center flex-wrap gap-4 text-xl md:text-2xl font-bold font-mono">
              <span className="text-white">Come here</span>
              <ChevronRight className="text-accent animate-pulse" size={28} />
              <span className="text-accent">Complete Challenges</span>
              <ChevronRight className="text-accent animate-pulse" size={28} />
              <span className="text-white">Learn & Level Up</span>
              <ChevronRight className="text-accent animate-pulse" size={28} />
              <span className="text-accent-blue">Career Guidance Guaranteed!</span>
            </div>
          </div>

          <p className="text-gray-300 text-xl font-semibold max-w-3xl mx-auto">
            Put your defensive security skills to the test with hands-on challenges designed by industry professionals.
          </p>
        </div>

        {/* Challenges Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {challenges.map((challenge) => {
            const Icon = challenge.icon;
            return (
              <div
                key={challenge.id}
                className={`bg-secondary border-2 border-accent/20 rounded-lg p-8 hover:border-accent transition-all duration-300 transform hover:-translate-y-2 ${
                  challenge.status === 'coming-soon' ? 'opacity-75' : ''
                }`}
              >
                {/* Challenge Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${challenge.color} flex items-center justify-center`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <span className={`px-4 py-1 rounded-full text-sm font-bold border ${getDifficultyColor(challenge.difficulty)}`}>
                    {challenge.difficulty}
                  </span>
                </div>

                {/* Challenge Info */}
                <h3 className="text-2xl font-extrabold text-white mb-2 font-mono">
                  {challenge.title}
                </h3>
                <p className="text-accent text-sm font-bold mb-4">{challenge.category}</p>
                <p className="text-gray-300 text-base font-semibold mb-6 leading-relaxed">
                  {challenge.description}
                </p>

                {/* Credentials for Elasticsearch */}
                {challenge.credentials && (
                  <div className="bg-primary border border-accent/20 rounded-lg p-4 mb-4">
                    <p className="text-accent text-sm font-bold mb-2 flex items-center">
                      <Key size={16} className="mr-2" />
                      Access Credentials:
                    </p>
                    <div className="space-y-1 font-mono text-sm">
                      <p className="text-gray-300">
                        <span className="text-gray-500">Username:</span> <span className="text-white font-bold">{challenge.credentials.username}</span>
                      </p>
                      <p className="text-gray-300">
                        <span className="text-gray-500">Password:</span> <span className="text-white font-bold break-all">{challenge.credentials.password}</span>
                      </p>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  {challenge.url && challenge.status === 'active' && (
                    <a
                      href={challenge.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-accent hover:bg-accent-blue text-primary font-bold py-3 px-6 rounded-lg font-mono text-center transition-all duration-300 transform hover:scale-105"
                    >
                      <span className="flex items-center justify-center">
                        Start Challenge
                        <ExternalLink className="ml-2 w-5 h-5" />
                      </span>
                    </a>
                  )}

                  {challenge.cheatsheet && (
                    <a
                      href={challenge.cheatsheet}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-primary border-2 border-accent/30 hover:border-accent text-accent font-bold py-3 px-6 rounded-lg font-mono text-center transition-all duration-300"
                    >
                      <span className="flex items-center justify-center">
                        View Cheatsheet
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </span>
                    </a>
                  )}

                  {challenge.submitUrl && (
                    <a
                      href={challenge.submitUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-accent-blue/20 border-2 border-accent-blue/50 hover:bg-accent-blue/30 text-accent-blue font-bold py-3 px-6 rounded-lg font-mono text-center transition-all duration-300"
                    >
                      <span className="flex items-center justify-center">
                        Submit Flags
                        <Trophy className="ml-2 w-5 h-5" />
                      </span>
                    </a>
                  )}

                  {challenge.status === 'coming-soon' && (
                    <div className="w-full bg-gray-700/50 text-gray-400 font-bold py-3 px-6 rounded-lg font-mono text-center cursor-not-allowed">
                      Coming Soon...
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Discord CTA */}
        <div className="bg-gradient-to-r from-accent/10 to-accent-blue/10 border-2 border-accent/20 rounded-lg p-8 md:p-12 text-center">
          <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-4 font-mono">
            Need Help? Join Our Community!
          </h3>
          <p className="text-gray-300 text-lg font-semibold mb-6 max-w-2xl mx-auto">
            Get hints, discuss solutions, and connect with fellow defenders on our Discord server.
          </p>
          <a
            href="https://discord.com/invite/EYmWz5Cu"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-accent hover:bg-accent-blue text-primary font-extrabold py-4 px-10 rounded-lg font-mono text-xl transition-all duration-300 transform hover:scale-105"
          >
            Join Discord Community
          </a>
        </div>
      </div>
    </div>
  );
};

export default Challenges;
